import classNames from "classnames";
import React, {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef
} from "react";

interface OtpInputProps {
  /** Current 6-digit OTP value */
  value: string;
  /** Callback when OTP value changes */
  onChange: (value: string) => void;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Whether to show error state */
  error?: boolean;
  /** Number of digits (default: 6) */
  length?: number;
  /** Called when Enter is pressed */
  onEnter?: () => void;
}

/**
 * OTP Input Component
 *
 * Displays separate input boxes for each digit of a verification code.
 * - Auto-focuses next box on digit entry
 * - Backspace navigates to previous box
 * - Supports paste of full code
 * - Empty boxes display underscore placeholder
 */
export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  length = 6,
  onEnter
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (!disabled && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  const focusInput = (index: number) => {
    if (index >= 0 && index < length && inputRefs.current[index]) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const digit = e.target.value.slice(-1); // Get last character

    // Only allow digits
    if (digit && !/^\d$/.test(digit)) {
      return;
    }

    // Update value
    const newValue = value.split("");
    while (newValue.length < length) {
      newValue.push("");
    }
    newValue[index] = digit;
    onChange(newValue.join("").slice(0, length));

    // Auto-focus next input if digit was entered
    if (digit && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Enter: submit if fully filled
    if (e.key === "Enter") {
      e.preventDefault();
      onEnter?.();
    }
    // Backspace: clear current and move to previous
    else if (e.key === "Backspace") {
      e.preventDefault();

      const newValue = value.split("");
      while (newValue.length < length) {
        newValue.push("");
      }

      // If current box has value, clear it
      if (newValue[index]) {
        newValue[index] = "";
        onChange(newValue.join("").slice(0, length));
      }
      // If current box is empty, move to previous and clear it
      else if (index > 0) {
        newValue[index - 1] = "";
        onChange(newValue.join("").slice(0, length));
        focusInput(index - 1);
      }
    }
    // Arrow left
    else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }
    // Arrow right
    else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text/plain");
    const digits = pastedData.replace(/\D/g, "").slice(0, length);

    if (digits) {
      onChange(digits);

      // Focus the next empty box or last box
      const nextIndex = Math.min(digits.length, length - 1);
      focusInput(nextIndex);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select text on focus for easy overwrite
    e.target.select();
  };

  // Get current value as array with padding
  const valueArray = value.padEnd(length, "").split("");

  return (
    <div
      className="flex gap-xs items-center"
      role="group"
      aria-label="Verification code input"
    >
      {Array.from({ length }).map((_, index) => {
        const hasValue = !!valueArray[index];

        return (
          <div key={index} className="relative">
            <input
              autoComplete="off"
              aria-label={`Digit ${index + 1} of ${length}`}
              className={classNames(
                "peer w-[3.125rem] py-md text-center text-t-xl font-guardian text-dark-blue",
                "border rounded-tl-br-sm transition-short-in-out",
                "focus-outline",
                {
                  "border-neutral-200": !error && !hasValue,
                  "border-dark-blue": !error && hasValue,
                  "border-pink-1200": error,
                  "hover:border-turquoise-600": !error && !disabled && !hasValue,
                  "cursor-not-allowed": disabled
                }
              )}
              disabled={disabled}
              inputMode="numeric"
              maxLength={1}
              onChange={e => handleChange(index, e)}
              onFocus={handleFocus}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
              pattern="\d*"
              ref={el => (inputRefs.current[index] = el)}
              size={1}
              type="text"
              value={valueArray[index] || ""}
            />

            {!hasValue && (
              <span
                aria-hidden="true"
                className={classNames(
                  "absolute left-1/2 bottom-md -translate-x-1/2 w-md h-[2px]",
                  "bg-t-neutral-600 pointer-events-none transition-short-in-out",
                  "peer-focus-visible:bg-dark-blue"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OtpInput;
